<template>
  <section>
    <custom-design
      title="BACK"
      :base-tag-svg-img-path="baseTagSvgImgPath ? getImage(baseTagSvgImgPath) : false"
      :design-svg-img-path="
        showRedesignedTagBack
          ? false
          : (backSvgImgPath ? getImage(backSvgImgPath) : require('@/assets/images/dyot/TagPersonalization.svg')) || false
      "
      :pet-name-text="showRedesignedTagBack ? redesignedTextConfig.petNameText : personalizationTextConfig.petNameText"
      :phone-text="showRedesignedTagBack ? redesignedTextConfig.phoneText : personalizationTextConfig.phoneText"
      :home-safe-id-text="showRedesignedTagBack ? redesignedTextConfig.homeSafeIdText : personalizationTextConfig.homeSafeIdText"
      :left-zone-line1-text="showRedesignedTagBack ? redesignedTextConfig.leftZoneLine1Text : false"
      :left-zone-line2-text="showRedesignedTagBack ? redesignedTextConfig.leftZoneLine2Text : false"
      :hotline-text="showRedesignedTagBack ? redesignedTextConfig.hotlineText : false"
      :url-text="showRedesignedTagBack ? redesignedTextConfig.urlText : false"
      :show-redesigned-tag-back="showRedesignedTagBack"
      :show-labels="showLabels"
      :scale="scale"
      :height="height"
      :boundary-padding="25"
      canvas-b-g-color="#f7f7f7"
    />
  </section>
</template>

<script>
import getImage from '../mixins/getImage';
import { homeSafeCanvasStyles } from '../mixins/homeSafeCanvasStyles';
import CustomDesign from './CustomDesign';

export default {
  name: 'PersonalizePreview',
  components: { CustomDesign },
  mixins: [getImage, homeSafeCanvasStyles],
  props: {
    personalizationSelected: {
      type: Boolean,
      default: true,
      required: false,
    },
    baseTagSvgImgPath: {
      type: [String, Boolean],
      default: null,
      required: false,
    },
    backSvgImgPath: {
      type: [String, Boolean],
      default: null,
      required: false,
    },
    showLabels: {
      type: Boolean,
      default: true,
      required: false,
    },
    height: {
      type: Number,
      default: 500,
      required: false,
    },
    scale: {
      type: Number,
      default: 0.8,
      required: false,
    },
    petName: {
      type: String,
      default: 'Found me?',
      required: true,
    },
    homeSafeId: {
      type: String,
      default: 'U237JC9Z',
      required: false,
    },
    phoneNumber: {
      type: [String, Number],
      default: '',
      required: true,
    },
    showRedesignedTagBack: {
      type: Boolean,
      default: false,
      required: false,
    },
    leftZoneLine1: {
      type: String,
      default: 'Scan here',
      required: false,
    },
    leftZoneLine2: {
      type: String,
      default: null,
      required: false,
    },
    hotlineNumber: {
      type: String,
      default: '',
      required: false,
    },
    tagBackUrl: {
      type: String,
      default: 'www.docupet.com',
      required: false,
    },
    defaultPhoneNumber: {
      type: [String, Number],
      default: '',
      required: false,
    },
  },
  computed: {
    leftZoneLine1Text() {
      return {
        offsetX: this.leftZoneLine2 ? 148 : 118,
        offsetY: 0,
        boundaryPadding: -74,
        color: 'black',
        text: this.leftZoneLine1,
        minFontSize: 18,
        maxFontSize: this.leftZoneLine2 ? 28 : 30,
        fontFamily: 'Lato',
        options: {
          fontSize: this.leftZoneLine2 ? 28 : 30,
          angle: -90,
          fontWeight: 400,
        },
      };
    },
    leftZoneLine2Text() {
      if (!this.leftZoneLine2) return false;
      return {
        offsetX: 118,
        offsetY: 0,
        boundaryPadding: -74,
        color: 'black',
        text: this.leftZoneLine2,
        minFontSize: 18,
        maxFontSize: 28,
        fontFamily: 'Lato',
        options: {
          fontSize: 28,
          angle: -90,
          fontWeight: 400,
        },
      };
    },
    hasCustomPhoneNumber() {
      if (!this.personalizationSelected || !this.phoneNumber) return false;
      const enteredDigits = this.phoneNumber.toString().replace(/\D/g, '');
      const defaultDigits = this.defaultPhoneNumber?.toString()?.replace(/\D/g, '') || '';
      return enteredDigits.length > 0 && enteredDigits !== defaultDigits;
    },
    showPhoneLine() {
      if (!this.personalizationSelected) return false;
      const enteredDigits = this.phoneNumber?.toString()?.replace(/\D/g, '') || '';
      const defaultDigits = this.defaultPhoneNumber?.toString()?.replace(/\D/g, '') || '';
      return !(defaultDigits.length > 0 && enteredDigits === defaultDigits);
    },
    redesignedTextConfig() {
      return {
        petNameText: {
          offsetX: 0,
          offsetY: this.showPhoneLine ? -154 : -134,
          boundaryPadding: 60,
          color: 'black',
          text: this.petName || (this.personalizationSelected ? '(Pet Name)' : 'Found me?'),
          minFontSize: 24,
          maxFontSize: 36,
          maxCharacters: 10,
          fontFamily: 'Lato',
          options: {
            fontSize: 24,
          },
        },
        phoneText: this.showPhoneLine
          ? {
              offsetX: 0,
              offsetY: -115,
              boundaryPadding: 60,
              color: 'black',
              text: this.hasCustomPhoneNumber ? this.phoneNumberFormatter(this.phoneNumber) : '(Phone Number)',
              minFontSize: 24,
              maxFontSize: 36,
              fontFamily: 'Lato',
              options: {
                fontSize: 24,
              },
            }
          : false,
        homeSafeIdText: {
          offsetX: -118,
          offsetY: 0,
          boundaryPadding: 20,
          color: 'black',
          text: this.homeSafeId?.toUpperCase(),
          minFontSize: 30,
          maxFontSize: 32,
          maxCharacters: 8,
          fontFamily: 'Lato',
          options: {
            fontSize: 32,
            angle: 90,
          },
        },
        leftZoneLine1Text: this.leftZoneLine1Text,
        leftZoneLine2Text: this.leftZoneLine2Text,
        hotlineText: {
          offsetX: 0,
          offsetY: 154,
          boundaryPadding: 30,
          color: 'black',
          text: this.hotlineNumber,
          minFontSize: 24,
          maxFontSize: 36,
          fontFamily: 'Lato',
          options: {
            fontSize: 24,
          },
        },
        urlText: {
          offsetX: 0,
          offsetY: 117,
          boundaryPadding: 30,
          color: 'black',
          text: this.tagBackUrl,
          minFontSize: 24,
          maxFontSize: 32,
          fontFamily: 'Lato',
          options: {
            fontSize: 24,
          },
        },
      };
    },
    personalizationTextConfig() {
      return {
        petNameText: {
          offsetX: 0,
          offsetY: -100,
          boundaryPadding: 60,
          color: 'black',
          text: this.petName || '(Pet Name)',
          minFontSize: 41,
          maxFontSize: 42,
          fontFamily: 'Lato',
          options: {
            fontSize: 42,
          },
          ...(this.showLabels && this.personalizationSelected
            ? {
                labelOptions: {
                  text: this.$t('personalizePreview.labelOptionsText'),
                  offsetX: 20,
                  offsetY: -60,
                  fontWeight: 600,
                  fontFamily: 'Lato',
                  fontSize: 42,
                },
                lineOptions: {
                  offsetX1: 1.55,
                  offsetY1: -5,
                  offsetX2: 15,
                  offsetY2: 15,
                },
              }
            : {}),
        },
        homeSafeIdText: {
          offsetX: 0,
          offsetY: 25,
          boundaryPadding: 60,
          color: 'black',
          text: this.homeSafeId?.toUpperCase(),
          minFontSize: 41,
          maxFontSize: 42,
          fontFamily: 'Lato',
          options: {
            fontSize: 42,
          },
          ...(this.showLabels
            ? {
                labelOptions: {
                  text: this.$t('personalizePreview.labelOptionsText2'),
                  offsetX: 50,
                  offsetY: -70,
                  fontFamily: 'Lato',
                  fontSize: 32,
                  styles: this.buildHomeSafeCanvasStyles(this.$t('personalizePreview.labelOptionsText2'), {
                    fontSize: 32,
                    fill: '#003C71',
                  }),
                },
                lineOptions: {
                  offsetX1: 1.7,
                  offsetY1: -10,
                  offsetX2: 10,
                  offsetY2: 0,
                },
              }
            : {}),
        },
        phoneText: {
          offsetX: 0,
          offsetY: 85,
          boundaryPadding: 30,
          color: 'black',
          text:
            this.personalizationSelected !== null && this.phoneNumber
              ? (this.personalizationSelected === false ? '1-' : '') + this.phoneNumberFormatter(this.phoneNumber)
              : '(123-Your-Number)',
          minFontSize: 37,
          maxFontSize: 42,
          fontFamily: 'Lato',
          options: {
            fontSize: 38,
          },
          ...(this.showLabels && this.personalizationSelected
            ? {
                labelOptions: {
                  text: this.$t('personalizePreview.labelOptionsText3'),
                  offsetX: -25,
                  offsetY: 70,
                  fontFamily: 'Lato',
                  styles: this.buildHomeSafeCanvasStyles(this.$t('personalizePreview.labelOptionsText3'), (lineIndex) =>
                    lineIndex <= 1 ? { fontWeight: 600, fill: '#003C71' } : { fontSize: 32, fill: '#003C71' }
                  ),
                },
                lineOptions: {
                  offsetX1: 1.35,
                  offsetY1: 0,
                  offsetX2: 15,
                  offsetY2: -95,
                },
              }
            : {}),
        },
      };
    },
  },
  methods: {
    phoneNumberFormatter(phone) {
      const phoneStr = phone != null ? String(phone) : '';
      if (phoneStr.length > 0) {
        let x = phoneStr.replace(/\D/g, '').match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
        return !x[2] ? x[1] : x[1] + '-' + x[2] + (x[3] ? '-' + x[3] : '');
      }
      return '';
    },
  },
};
</script>